/* eslint-disable complexity */
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import SelectCountry from 'content/pages/SetupDevicesPage/SelectCountry';
import SetupDevicePassword from 'content/pages/SetupDevicesPage/SetupDevicePassword';
import SetupDigitalInput from 'content/pages/SetupDevicesPage/SetupDigitalInput';
import SetupGrid from 'content/pages/SetupDevicesPage/SetupGrid';
import SetupLoadBalancing from 'content/pages/SetupDevicesPage/SetupLoadBalancing';
import SetupNetwork from 'content/pages/SetupDevicesPage/SetupNetwork';
import SetupOCPP from 'content/pages/SetupDevicesPage/SetupOCPP';
import SetupTechnicianPassword from 'content/pages/SetupDevicesPage/SetupTechnicianPassword';
import useCharger from 'hooks/useCharger';
import useIsDigitalInputSupported from 'hooks/useIsDigitalInputSupported';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import {
  deviceIsCharger,
  deviceIsController,
  isDeviceProCharger,
} from 'utils/common/deviceTypeUtils';
import type { DefaultPopupProps } from 'utils/props';

import type { Countries } from 'constants/countryPreset';
import countryPresets, { countryDelayPresets } from 'constants/countryPreset';

import Button from 'components/Button';
import Modal from 'components/Modal';

import { useAppDispatch, useAppSelector } from 'redux-store';
import { groupConfigDevices } from 'redux-store/slices/groupConfigDevices';
import type { GroupConfigDevicesSettingsRequestBody } from 'redux-store/slices/groupConfigDevices/types';
import {
  setDeviceOverviewEditMode,
  setDeviceOverviewSelectedDevices,
} from 'redux-store/slices/ui';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';
import groupConfigDevicesThunk from 'redux-store/thunks/groupConfigDevices/groupConfigDevicesThunk';

import DeviceTypeCount from './DeviceTypeCount';
import GroupConfiguration from './GroupConfiguration';

interface DeviceGroupConfigModalProps extends DefaultPopupProps {
  deviceSerialNumbers?: string[];
  isSingleDeviceConfig: boolean;
}

export type DeviceConfigSections =
  | 'country'
  | 'digitalInput'
  | 'grid'
  | 'technicianPassword'
  | 'devicePassword'
  | 'ocpp'
  | 'loadBalancing'
  | 'wifiNetwork';

const DeviceGroupConfigModal: FC<DeviceGroupConfigModalProps> = ({
  open,
  onClose,
  deviceSerialNumbers,
  isSingleDeviceConfig,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useProfile();

  const deviceList = useAppSelector(state => state.devices.items);

  const filteredDevices = useMemo(() => {
    return deviceList.filter(device =>
      (deviceSerialNumbers || []).includes(device.serial),
    );
  }, [deviceList, deviceSerialNumbers]);

  const countChargers = filteredDevices.filter(device =>
    deviceIsCharger(device.type),
  ).length;

  const countControllers = filteredDevices.filter(device =>
    deviceIsController(device.type),
  ).length;

  const singleDeviceSerial =
    filteredDevices.length === 1 ? filteredDevices[0].serial : undefined;

  const devicetType = useCharger(singleDeviceSerial, state => state?.typ);

  const isProCharger = isDeviceProCharger(devicetType);

  const deviceType = filteredDevices.find(
    device => device.serial === singleDeviceSerial,
  )?.type;

  const isDigitalInputSupported = useIsDigitalInputSupported(
    singleDeviceSerial,
    deviceType,
  );

  const [section, setSection] = useState<DeviceConfigSections | null>(null);

  // Country selection
  const [country, setCountry] = useState<Countries | null>(null);
  const [isLoadingCountry, setIsLoadingCountry] = useState<boolean>(false);
  const [saveCountry, setSaveCountry] = useState(false);

  // grid configuration
  const [maximumChargingCurrent, setMaximumChargingCurrent] = useState(0);
  const [maximumSinglePhaseCurrent, setMaximumSinglePhaseCurrent] = useState(0);
  const [schedulerStart, setSchedulerStart] = useState(0);
  const [schedulerEnd, setSchedulerEnd] = useState(0);
  const [flexibleTariffStart, setFlexibleTariffStart] = useState(0);
  const [flexibleTariffEnd, setFlexibleTariffEnd] = useState(0);
  const [afterPowerOutage, setAfterPowerOutage] = useState(0);
  const [isLoadingGrid, setIsLoadingGrid] = useState<boolean>(false);
  const [gridInputError, setGridInputError] = useState<boolean>(false);
  const [saveGridSettings, setSaveGridSettings] = useState(false);

  // digital input
  const [digitalInput, setDigitalInput] = useState(false);
  const [allowCharging, setAllowCharging] = useState(true);
  const [digitalInputLimit, setDigitalInputLimit] = useState(6);
  const [digitalInput1Phase, setDigitalInput1Phase] = useState(false);
  const [digitalInputInverted, setDigitalInputInverted] = useState('closed');
  const [isLoadingDigitalInput, setIsLoadingDigitalInput] =
    useState<boolean>(false);
  const [saveDigitalInput, setSaveDigitalInput] = useState(false);

  // technician password
  const [technicianPassword, setTechnicianPassword] = useState<string>('');
  const [protectGridRequirement, setProtectGridRequirement] = useState(true);
  const [protectDigitalInput, setProtectDigitalInput] = useState(false);
  const [protectLoadbalancing, setProtectLoadbalancing] = useState(false);
  const [isLoadingTechnicianPassword, setIsLoadingTechnicianPassword] =
    useState<boolean>(false);
  const [saveTechnicianPassword, setSaveTechnicianPassword] = useState(false);

  // ocpp
  const [ocppUrl, setOcppUrl] = useState<string>('');
  const [appendSerialNumber, setAppendSerialNumber] = useState<boolean>(false);
  const [isLoadingOcpp, setIsLoadingOcpp] = useState<boolean>(false);
  const [saveOcppSettings, setSaveOcppSettings] = useState(false);

  // load balancing
  const [loadBalancingGroupId, setLoadBalancingGroupId] = useState<string>('');
  const [loadBalancingEnabled, setLoadBalancingEnabledChange] =
    useState<boolean>(false);
  const [maxTotalCurrent, setMaxTotalCurrent] = useState<number>(32);
  const [maxGridCurrent, setMaxGridCurrent] = useState<number>(32);
  const [loadBalancingInputError, setLoadBalancingInputError] =
    useState<boolean>(false);
  const [isLoadingLoadBalancing, setIsLoadingLoadBalancing] =
    useState<boolean>(false);
  const [saveLoadBalancing, setSaveLoadBalancing] = useState(false);

  // Device password
  const [devicePassword, setDevicePassword] = useState<string>('');
  const [isLoadingDevicePassword, setIsLoadingDevicePassword] =
    useState<boolean>(false);
  const [saveDevicePassword, setSaveDevicePassword] = useState(false);

  // wifi network
  const [isLoadingNetwork, setIsLoadingNetwork] = useState<boolean>(false);
  const [ssid, setSsid] = useState<string>('');
  const [networkPassword, setNetworkPassword] = useState<string>('');

  const [saveWifiNetwork, setSaveWifiNetwork] = useState(false);

  useEffect(() => {
    if (country) {
      const preset = countryDelayPresets[country];
      const currentMax = countryPresets[country];

      setDigitalInputLimit(currentMax.la1);
      setMaximumChargingCurrent(32);
      setMaximumSinglePhaseCurrent(currentMax.la1);
      setSchedulerStart(preset.rdbs);
      setSchedulerEnd(preset.rdes);
      setFlexibleTariffStart(preset.rdbf);
      setFlexibleTariffEnd(preset.rdef);
      setAfterPowerOutage(preset.rdre);
    }
  }, [country]);

  const clearCountryInputs = (): void => {
    setCountry(null);
    setIsLoadingCountry(false);
    setSaveCountry(false);
  };

  const clearGridInputs = (): void => {
    setMaximumChargingCurrent(0);
    setMaximumSinglePhaseCurrent(0);
    setSchedulerStart(0);
    setSchedulerEnd(0);
    setFlexibleTariffStart(0);
    setFlexibleTariffEnd(0);
    setAfterPowerOutage(0);
    setIsLoadingGrid(false);
    setSaveGridSettings(false);
  };

  const clearDigitalInputs = (): void => {
    setDigitalInput(false);
    setAllowCharging(true);
    setDigitalInputLimit(6);
    setDigitalInput1Phase(false);
    setDigitalInputInverted('closed');
    setIsLoadingDigitalInput(false);
    setSaveDigitalInput(false);
  };

  const clearTechPasswordInputs = (): void => {
    setTechnicianPassword('');
    setProtectGridRequirement(true);
    setProtectDigitalInput(false);
    setProtectLoadbalancing(false);
    setIsLoadingTechnicianPassword(false);
    setSaveTechnicianPassword(false);
  };

  const clearDevicePasswordInputs = (): void => {
    setDevicePassword('');
    setIsLoadingDevicePassword(false);
    setSaveDevicePassword(false);
  };

  const clearOcppInputs = (): void => {
    setOcppUrl('');
    setAppendSerialNumber(false);
    setIsLoadingOcpp(false);
    setSaveOcppSettings(false);
  };

  const clearLoadBalancingInputs = (): void => {
    setLoadBalancingGroupId('');
    setLoadBalancingEnabledChange(false);
    setMaxTotalCurrent(32);
    setMaxGridCurrent(32);
    setLoadBalancingInputError(false);
    setIsLoadingLoadBalancing(false);
    setSaveLoadBalancing(false);
  };

  const clearWifiNetworkInputs = (): void => {
    setSsid('');
    setNetworkPassword('');
    setIsLoadingNetwork(false);
    setSaveWifiNetwork(false);
  };

  const handleOnClickCancel = useCallback((): void => {
    clearGridInputs();
    clearCountryInputs();
    clearDigitalInputs();
    clearLoadBalancingInputs();
    clearTechPasswordInputs();
    clearDevicePasswordInputs();
    clearOcppInputs();
    clearWifiNetworkInputs();
    setSection(null);
  }, []);

  const setLoadingStates = useCallback((): void => {
    if (saveCountry) setIsLoadingCountry(true);
    if (saveGridSettings) setIsLoadingGrid(true);
    if (saveDigitalInput) setIsLoadingDigitalInput(true);
    if (saveTechnicianPassword) setIsLoadingTechnicianPassword(true);
    if (saveDevicePassword) setIsLoadingDevicePassword(true);
    if (saveLoadBalancing) setIsLoadingLoadBalancing(true);
    if (saveOcppSettings) setIsLoadingOcpp(true);
    if (saveWifiNetwork) setIsLoadingNetwork(true);
  }, [
    saveCountry,
    saveDevicePassword,
    saveDigitalInput,
    saveGridSettings,
    saveLoadBalancing,
    saveOcppSettings,
    saveTechnicianPassword,
    saveWifiNetwork,
  ]);

  const handleGroupConfiguration = useCallback(async () => {
    dispatch(groupConfigDevices([]));
    try {
      setLoadingStates();

      const normalizedOcppUrl =
        singleDeviceSerial && ocppUrl.endsWith(singleDeviceSerial)
          ? ocppUrl.slice(0, -singleDeviceSerial.length)
          : ocppUrl;

      const data: {
        serials: string[] | undefined;
        configurations: GroupConfigDevicesSettingsRequestBody;
      } = {
        serials: deviceSerialNumbers,
        configurations: {},
      };

      if (saveCountry && country) {
        data.configurations.country = {
          cy: country,
        };
      }

      if (saveGridSettings) {
        const gridSettings = {
          ama: maximumChargingCurrent,
          rdbs: schedulerStart,
          rdes: schedulerEnd,
          rdbf: flexibleTariffStart,
          rdef: flexibleTariffEnd,
          rdre: afterPowerOutage,
          ...(isProCharger ? {} : { la1: maximumSinglePhaseCurrent }),
        };

        data.configurations.gridSettings = gridSettings;
      }

      if (saveDigitalInput) {
        data.configurations.digitalInput = {
          die: digitalInput,
          currentLimit: digitalInputLimit,
          allowCharging: allowCharging,
          di1: digitalInput1Phase,
          dii: digitalInputInverted === 'closed' ? true : false,
        };
      }

      if (saveOcppSettings) {
        data.configurations.ocpp = {
          ocppu: normalizedOcppUrl,
          appendSerialNumber: appendSerialNumber,
        };
      }

      if (saveTechnicianPassword) {
        data.configurations.technicianPassword = {
          ipw: technicianPassword,
          pgr: protectGridRequirement,
          pdi: protectDigitalInput,
          lopr: protectLoadbalancing,
        };
      }

      if (saveDevicePassword) {
        data.configurations.devicePassword = {
          pass: devicePassword,
        };
      }

      if (saveLoadBalancing) {
        data.configurations.loadBalancing = {
          log: loadBalancingGroupId,
          loe: loadBalancingEnabled,
          lot: {
            amp: maxTotalCurrent,
            sta: maxTotalCurrent,
            dyn: maxGridCurrent,
            ts: Math.max(Math.floor(new Date().getTime() / 1000) + 1),
          },
        };
      }

      if (saveWifiNetwork) {
        data.configurations.networkDetails = [
          {
            ssid: ssid,
            key: networkPassword,
          },
        ];
      }

      const response = await dispatch(
        groupConfigDevicesThunk({ groupConfigDevices: data }),
      ).unwrap();

      const filteredResults = response.results.filter(
        device => device.pending !== true,
      );

      dispatch(groupConfigDevices(filteredResults));

      if (profile) {
        await dispatch(getAllDevicesThunk({ userId: profile.id }));
      }
    } catch (e) {
      Sentry.captureException(e, {
        tags: { page: 'Device Configuration' },
      });
    } finally {
      dispatch(setDeviceOverviewEditMode(false));
      dispatch(setDeviceOverviewSelectedDevices([]));
      handleOnClickCancel();
      onClose();
    }
  }, [
    dispatch,
    setLoadingStates,
    singleDeviceSerial,
    ocppUrl,
    deviceSerialNumbers,
    saveCountry,
    country,
    saveGridSettings,
    saveDigitalInput,
    saveOcppSettings,
    saveTechnicianPassword,
    saveDevicePassword,
    saveLoadBalancing,
    saveWifiNetwork,
    profile,
    maximumChargingCurrent,
    schedulerStart,
    schedulerEnd,
    flexibleTariffStart,
    flexibleTariffEnd,
    afterPowerOutage,
    isProCharger,
    maximumSinglePhaseCurrent,
    digitalInput,
    digitalInputLimit,
    allowCharging,
    digitalInput1Phase,
    digitalInputInverted,
    appendSerialNumber,
    technicianPassword,
    protectGridRequirement,
    protectDigitalInput,
    protectLoadbalancing,
    devicePassword,
    loadBalancingGroupId,
    loadBalancingEnabled,
    maxTotalCurrent,
    maxGridCurrent,
    ssid,
    networkPassword,
    handleOnClickCancel,
    onClose,
  ]);

  const handleOnClickOpen = (openSection: DeviceConfigSections): void => {
    setSection(openSection);
  };

  const handleOnClickSave = (currentSection: DeviceConfigSections): void => {
    setSection(null);

    switch (currentSection) {
      case 'country':
        setIsLoadingCountry(false);
        setSaveCountry(true);
        setSaveGridSettings(true);
        break;
      case 'grid':
        setIsLoadingGrid(false);
        setSaveGridSettings(true);
        break;
      case 'digitalInput':
        setIsLoadingDigitalInput(false);
        setSaveDigitalInput(true);
        break;
      case 'technicianPassword':
        setIsLoadingTechnicianPassword(false);
        setSaveTechnicianPassword(true);
        break;
      case 'devicePassword':
        setIsLoadingDevicePassword(false);
        setSaveDevicePassword(true);
        break;
      case 'ocpp':
        setIsLoadingOcpp(false);
        setSaveOcppSettings(true);
        break;
      case 'loadBalancing':
        setIsLoadingLoadBalancing(false);
        setSaveLoadBalancing(true);
        break;
      case 'wifiNetwork':
        setIsLoadingNetwork(false);
        setSaveWifiNetwork(true);
        break;
    }
  };

  const handleOnClickDiscard = (currentSection: DeviceConfigSections): void => {
    setSection(null);

    switch (currentSection) {
      case 'country':
        clearCountryInputs();
        break;
      case 'grid':
        clearGridInputs();
        break;
      case 'digitalInput':
        clearDigitalInputs();
        break;
      case 'technicianPassword':
        clearTechPasswordInputs();
        break;
      case 'devicePassword':
        clearDevicePasswordInputs();
        break;
      case 'ocpp':
        clearOcppInputs();
        break;
      case 'loadBalancing':
        clearLoadBalancingInputs();
        break;
      case 'wifiNetwork':
        clearWifiNetworkInputs();
        break;
    }
  };

  const sectionValidators = {
    country: () => isLoadingCountry || country === null,
    grid: () => isLoadingGrid || gridInputError || maximumChargingCurrent === 0,
    digitalInput: () => isLoadingDigitalInput,
    technicianPassword: () =>
      isLoadingTechnicianPassword || technicianPassword.length < 8,
    devicePassword: () => isLoadingDevicePassword || devicePassword.length < 8,
    ocpp: () => isLoadingOcpp || !ocppUrl,
    loadBalancing: () =>
      isLoadingLoadBalancing ||
      loadBalancingInputError ||
      !loadBalancingGroupId,
    joinOrCreateGroup: () => isLoadingLoadBalancing || !loadBalancingGroupId,
    wifiNetwork: () => isLoadingNetwork || ssid.length < 1,
  };

  const getDisabledButtonState = (
    currentSection: DeviceConfigSections,
  ): boolean => sectionValidators[currentSection]();

  const onCloseProxy = useCallback((): void => {
    if (section === null) {
      onClose();
      setSection(null);
      handleOnClickCancel();
    } else {
      setSection(null);
    }
  }, [handleOnClickCancel, onClose, section]);

  const renderSelectedView = (
    selectedView: DeviceConfigSections,
  ): React.ReactElement => {
    switch (selectedView) {
      case 'country':
        return <SelectCountry country={country} setCountry={setCountry} />;
      case 'digitalInput':
        return (
          <SetupDigitalInput
            digitalInput={digitalInput}
            allowCharging={allowCharging}
            digitalInputLimit={digitalInputLimit}
            digitalInput1Phase={digitalInput1Phase}
            digitalInputInverted={digitalInputInverted}
            setDigitalInput={setDigitalInput}
            setAllowCharging={setAllowCharging}
            setDigitalInputLimit={setDigitalInputLimit}
            setDigitalInput1Phase={setDigitalInput1Phase}
            setDigitalInputInverted={setDigitalInputInverted}
            handleSubmitDigitalInputSetup={() =>
              handleOnClickSave('digitalInput')
            }
          />
        );
      case 'grid':
        return (
          <SetupGrid
            maximumChargingCurrent={maximumChargingCurrent}
            maximumSinglePhaseCurrent={maximumSinglePhaseCurrent}
            schedulerStart={schedulerStart}
            schedulerEnd={schedulerEnd}
            flexibleTariffStart={flexibleTariffStart}
            flexibleTariffEnd={flexibleTariffEnd}
            afterPowerOutage={afterPowerOutage}
            serial={singleDeviceSerial}
            setMaximumChargingCurrent={setMaximumChargingCurrent}
            setMaximumSinglePhaseCurrent={setMaximumSinglePhaseCurrent}
            setSchedulerStart={setSchedulerStart}
            setSchedulerEnd={setSchedulerEnd}
            setFlexibleTariffStart={setFlexibleTariffStart}
            setFlexibleTariffEnd={setFlexibleTariffEnd}
            setAfterPowerOutage={setAfterPowerOutage}
            setInputErrors={setGridInputError}
            handleSubmitGridSetup={() => handleOnClickSave('grid')}
          />
        );
      case 'technicianPassword':
        return (
          <SetupTechnicianPassword
            technicianPassword={technicianPassword}
            setTechnicianPassword={setTechnicianPassword}
            handleSubmitTechnicianPassword={() =>
              handleOnClickSave('technicianPassword')
            }
            protectGridRequirement={protectGridRequirement}
            protectDigitalInput={protectDigitalInput}
            protectLoadbalancing={protectLoadbalancing}
            setProtectGridRequirement={setProtectGridRequirement}
            setProtectDigitalInput={setProtectDigitalInput}
            setProtectLoadbalancing={setProtectLoadbalancing}
          />
        );
      case 'devicePassword':
        return (
          <SetupDevicePassword
            devicePassword={devicePassword}
            setDevicePassword={setDevicePassword}
            handleSubmitDeviceSetup={() => handleOnClickSave('devicePassword')}
          />
        );
      case 'ocpp':
        return (
          <SetupOCPP
            ocppUrl={ocppUrl}
            appendSerialNumber={appendSerialNumber}
            serial={singleDeviceSerial}
            setOcppUrl={setOcppUrl}
            setAppendSerialNumber={setAppendSerialNumber}
            handleSubmitOcppSetup={() => handleOnClickSave('ocpp')}
          />
        );
      case 'loadBalancing':
        return (
          <SetupLoadBalancing
            loadBalancingGroupId={loadBalancingGroupId}
            setLoadBalancingGroupId={setLoadBalancingGroupId}
            loadBalancingEnabled={loadBalancingEnabled}
            maxTotalCurrent={maxTotalCurrent}
            maxGridCurrent={maxGridCurrent}
            setLoadBalancingEnabledChange={setLoadBalancingEnabledChange}
            setMaxTotalCurrent={setMaxTotalCurrent}
            setMaxGridCurrent={setMaxGridCurrent}
            setInputErrors={setLoadBalancingInputError}
            isGroupConfig
            handleSubmitLoadBalancingSetup={() =>
              handleOnClickSave('loadBalancing')
            }
          />
        );
      case 'wifiNetwork':
        return (
          <SetupNetwork
            ssid={ssid}
            networkPassword={networkPassword}
            setSsid={setSsid}
            setNetworkPassword={setNetworkPassword}
            handleSubmitNetworkSetup={() => handleOnClickSave('wifiNetwork')}
          />
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      header={
        section
          ? t(
              'common:pages.devices.device_list.group_config.group_config_title',
              {
                devices: deviceSerialNumbers?.length,
              },
            )
          : ''
      }
      overrideCloseButtonIcon={
        section !== null ? <ArrowBackRoundedIcon /> : undefined
      }
      customComponent={
        section === null && !isSingleDeviceConfig ? (
          <DeviceTypeCount
            countChargers={countChargers}
            countControllers={countControllers}
          />
        ) : undefined
      }
    >
      {section === null ? (
        <GroupConfiguration
          countChargers={countChargers}
          countControllers={countControllers}
          ocppUrl={saveOcppSettings ? ocppUrl : null}
          selectedCountry={
            saveCountry && country
              ? `${t(`common:countries.${country}`)} (${
                  countryPresets[country].la1
                } A)`
              : null
          }
          grid={saveGridSettings ? `${maximumChargingCurrent} A` : null}
          technicianPassword={
            saveTechnicianPassword
              ? t(
                  'common:pages.devices.device_list.group_config.password_created',
                )
              : null
          }
          digitalInput={
            saveDigitalInput
              ? digitalInput
                ? t('common:on')
                : t('common:off')
              : null
          }
          loadBalancing={
            saveLoadBalancing
              ? loadBalancingEnabled
                ? loadBalancingGroupId
                : `${loadBalancingGroupId} (${t('common:disabled')})`
              : null
          }
          devicePassword={
            saveDevicePassword
              ? t(
                  'common:pages.devices.device_list.group_config.password_created',
                )
              : null
          }
          isDigitalInputSupported={
            singleDeviceSerial === undefined || isDigitalInputSupported
          }
          ssid={saveWifiNetwork ? ssid : null}
          isLoadingCountry={isLoadingCountry}
          isLoadingGrid={isLoadingGrid}
          isLoadingDigitalInput={isLoadingDigitalInput}
          isLoadingTechnicianPassword={isLoadingTechnicianPassword}
          isLoadingOcpp={isLoadingOcpp}
          isLoadingLoadBalancing={isLoadingLoadBalancing}
          isLoadingDevicePassword={isLoadingDevicePassword}
          isLoadingNetwork={isLoadingNetwork}
          isSingleDeviceConfig={isSingleDeviceConfig}
          handleGroupConfiguration={handleGroupConfiguration}
          handleOnClickOpen={handleOnClickOpen}
        />
      ) : (
        <Box display="flex" flexDirection="column">
          {renderSelectedView(section)}

          <Box mt={2}>
            <Button
              fullWidth
              variant="flat"
              size="large"
              disabled={getDisabledButtonState(section)}
              onClick={() => handleOnClickSave(section)}
            >
              {t('common:save')}
            </Button>
            <Box pt={1}>
              <Button
                variant="text"
                startIcon={<DeleteSweepOutlinedIcon />}
                size="large"
                sx={{
                  color: getDisabledButtonState(section)
                    ? 'grey.500'
                    : undefined,
                }}
                disabled={getDisabledButtonState(section)}
                onClick={() => handleOnClickDiscard(section)}
                fullWidth
              >
                {t(
                  'common:pages.devices.device_list.group_config.discard_config',
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default DeviceGroupConfigModal;
